import { useState, useEffect } from 'react';
import { bankAccountsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Building2, CreditCard, Globe } from 'lucide-react';

// Region configurations with payment methods and fields
const regionConfigs = {
  US: {
    label: 'United States',
    currency: 'USD',
    paymentMethods: ['ACH', 'Fedwire', 'ACH or Fedwire'],
    fields: ['routing_number', 'fedwire_routing'],
    fieldLabels: {
      routing_number: 'ACH Routing Number',
      fedwire_routing: 'Fedwire Routing Number',
    }
  },
  UK: {
    label: 'United Kingdom',
    currency: 'GBP',
    paymentMethods: ['FPS / CHAPS / BACS', 'BACS', 'FPS', 'CHAPS'],
    fields: ['sort_code', 'iban'],
    fieldLabels: {
      sort_code: 'Sort Code',
      iban: 'IBAN (Optional)',
    }
  },
  EU: {
    label: 'European Union',
    currency: 'EUR',
    paymentMethods: ['SEPA', 'SWIFT'],
    fields: ['iban', 'swift_code'],
    fieldLabels: {
      iban: 'IBAN',
      swift_code: 'BIC/SWIFT Code',
    }
  },
  IN: {
    label: 'India',
    currency: 'INR',
    paymentMethods: ['NEFT/RTGS/IMPS', 'SWIFT'],
    fields: ['ifsc_code', 'swift_code'],
    fieldLabels: {
      ifsc_code: 'IFSC Code (Local)',
      swift_code: 'SWIFT Code (International)',
    }
  },
  CA: {
    label: 'Canada',
    currency: 'CAD',
    paymentMethods: ['EFT', 'Wire', 'Interac'],
    fields: ['institution_number', 'transit_number', 'swift_code'],
    fieldLabels: {
      institution_number: 'Institution Number',
      transit_number: 'Transit Number',
      swift_code: 'SWIFT Code (International)',
    }
  },
};

const currencies = [
  { value: 'USD', label: 'USD ($)' },
  { value: 'GBP', label: 'GBP (£)' },
  { value: 'EUR', label: 'EUR (€)' },
  { value: 'INR', label: 'INR (₹)' },
  { value: 'CAD', label: 'CAD (C$)' },
];

const BankAccountsPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({
    account_name: '',
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    swift_code: '',
    routing_number: '',
    fedwire_routing: '',
    sort_code: '',
    iban: '',
    institution_number: '',
    transit_number: '',
    address: '',
    region: 'US',
    currency: 'USD',
    payment_method: '',
  });

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await bankAccountsApi.getAll();
      setAccounts(response.data);
    } catch (error) {
      toast.error('Failed to load bank accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (account = null) => {
    if (account) {
      setEditingAccount(account);
      setFormData({
        account_name: account.account_name,
        bank_name: account.bank_name,
        account_number: account.account_number,
        ifsc_code: account.ifsc_code || '',
        swift_code: account.swift_code || '',
        routing_number: account.routing_number || '',
        fedwire_routing: account.fedwire_routing || '',
        sort_code: account.sort_code || '',
        iban: account.iban || '',
        institution_number: account.institution_number || '',
        transit_number: account.transit_number || '',
        address: account.address || '',
        region: account.region,
        currency: account.currency,
        payment_method: account.payment_method || '',
      });
    } else {
      setEditingAccount(null);
      setFormData({
        account_name: '',
        bank_name: '',
        account_number: '',
        ifsc_code: '',
        swift_code: '',
        routing_number: '',
        fedwire_routing: '',
        sort_code: '',
        iban: '',
        institution_number: '',
        transit_number: '',
        address: '',
        region: 'US',
        currency: 'USD',
        payment_method: '',
      });
    }
    setDialogOpen(true);
  };

  const handleRegionChange = (region) => {
    const config = regionConfigs[region];
    setFormData({
      ...formData,
      region,
      currency: config?.currency || 'USD',
      payment_method: config?.paymentMethods?.[0] || '',
      // Clear region-specific fields
      ifsc_code: '',
      swift_code: '',
      routing_number: '',
      fedwire_routing: '',
      sort_code: '',
      iban: '',
      institution_number: '',
      transit_number: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAccount) {
        await bankAccountsApi.update(editingAccount.id, formData);
        toast.success('Bank account updated');
      } else {
        await bankAccountsApi.create(formData);
        toast.success('Bank account created');
      }
      setDialogOpen(false);
      fetchAccounts();
    } catch (error) {
      toast.error('Failed to save bank account');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this bank account?')) return;
    try {
      await bankAccountsApi.delete(id);
      toast.success('Bank account deleted');
      fetchAccounts();
    } catch (error) {
      toast.error('Failed to delete bank account');
    }
  };

  const getRegionBadgeClass = (region) => {
    const classes = {
      US: 'bg-blue-50 text-blue-700 border-blue-200',
      UK: 'bg-purple-50 text-purple-700 border-purple-200',
      EU: 'bg-amber-50 text-amber-700 border-amber-200',
      IN: 'bg-orange-50 text-orange-700 border-orange-200',
      CA: 'bg-red-50 text-red-700 border-red-200',
    };
    return classes[region] || 'bg-zinc-50 text-zinc-700';
  };

  const currentConfig = regionConfigs[formData.region];

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-zinc-900">Bank Accounts</h1>
          <p className="text-zinc-500 mt-1">Manage your payment accounts for different regions</p>
        </div>
        <Button 
          onClick={() => handleOpenDialog()}
          className="bg-zinc-900 hover:bg-zinc-800"
          data-testid="add-bank-account-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Bank Account
        </Button>
      </div>

      {/* Accounts Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-48 bg-zinc-100 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : accounts.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-zinc-200">
          <Building2 className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-zinc-700">No bank accounts yet</h3>
          <p className="text-zinc-500 mt-1 mb-4">Add bank accounts for different regions</p>
          <Button onClick={() => handleOpenDialog()} data-testid="add-first-bank-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Bank Account
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {accounts.map((account) => (
            <div 
              key={account.id}
              className="bg-white border border-zinc-200 rounded-xl p-6 hover:shadow-md transition-shadow"
              data-testid={`bank-account-card-${account.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-zinc-100 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-zinc-500" />
                  </div>
                  <div>
                    <p className="font-medium text-zinc-900 text-sm">{account.account_name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-mono border ${getRegionBadgeClass(account.region)}`}>
                        {account.region}
                      </span>
                      <span className="text-xs text-zinc-500">{account.currency}</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleOpenDialog(account)}
                    data-testid={`edit-bank-${account.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(account.id)}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    data-testid={`delete-bank-${account.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {account.payment_method && (
                <div className="mb-3 pb-3 border-b border-zinc-100">
                  <span className="text-xs text-zinc-500">Payment Method</span>
                  <p className="text-sm font-medium text-zinc-700">{account.payment_method}</p>
                </div>
              )}

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-500">Bank</span>
                  <span className="font-medium text-zinc-700 text-right">{account.bank_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Account</span>
                  <span className="font-mono text-zinc-700">{account.account_number}</span>
                </div>
                {account.routing_number && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">ACH Routing</span>
                    <span className="font-mono text-zinc-700">{account.routing_number}</span>
                  </div>
                )}
                {account.fedwire_routing && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Fedwire</span>
                    <span className="font-mono text-zinc-700">{account.fedwire_routing}</span>
                  </div>
                )}
                {account.sort_code && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Sort Code</span>
                    <span className="font-mono text-zinc-700">{account.sort_code}</span>
                  </div>
                )}
                {account.ifsc_code && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">IFSC</span>
                    <span className="font-mono text-zinc-700">{account.ifsc_code}</span>
                  </div>
                )}
                {account.swift_code && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">SWIFT</span>
                    <span className="font-mono text-zinc-700">{account.swift_code}</span>
                  </div>
                )}
                {account.iban && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">IBAN</span>
                    <span className="font-mono text-zinc-700 text-xs">{account.iban}</span>
                  </div>
                )}
                {account.institution_number && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Institution #</span>
                    <span className="font-mono text-zinc-700">{account.institution_number}</span>
                  </div>
                )}
                {account.transit_number && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Transit #</span>
                    <span className="font-mono text-zinc-700">{account.transit_number}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Globe className="w-5 h-5" />
              {editingAccount ? 'Edit Bank Account' : 'Add International Bank Account'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5 mt-4">
            {/* Region Selection - Key first step */}
            <div className="p-4 bg-zinc-50 rounded-lg space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Select Region</Label>
                <p className="text-xs text-zinc-500">This determines the required banking fields</p>
                <div className="grid grid-cols-5 gap-2">
                  {Object.entries(regionConfigs).map(([code, config]) => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => handleRegionChange(code)}
                      className={`p-3 rounded-lg border-2 transition-all text-center ${
                        formData.region === code
                          ? 'border-zinc-900 bg-white shadow-sm'
                          : 'border-zinc-200 hover:border-zinc-300 bg-white'
                      }`}
                      data-testid={`region-btn-${code}`}
                    >
                      <span className="font-mono text-sm font-bold">{code}</span>
                      <p className="text-xs text-zinc-500 mt-1">{config.currency}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="payment_method">Payment Method</Label>
                  <Select 
                    value={formData.payment_method} 
                    onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
                  >
                    <SelectTrigger data-testid="payment-method-select">
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                    <SelectContent>
                      {currentConfig?.paymentMethods?.map((method) => (
                        <SelectItem key={method} value={method}>
                          {method}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="currency">Currency</Label>
                  <Select 
                    value={formData.currency} 
                    onValueChange={(value) => setFormData({ ...formData, currency: value })}
                  >
                    <SelectTrigger data-testid="bank-currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map((curr) => (
                        <SelectItem key={curr.value} value={curr.value}>
                          {curr.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Account Details */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="account_name">Account Holder Name *</Label>
                <Input
                  id="account_name"
                  value={formData.account_name}
                  onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                  placeholder="OUTBOUND CLICK LLP"
                  required
                  data-testid="bank-account-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bank_name">Bank Name *</Label>
                <Input
                  id="bank_name"
                  value={formData.bank_name}
                  onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                  placeholder="Community Federal Savings Bank"
                  required
                  data-testid="bank-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="account_number">Account Number *</Label>
                <Input
                  id="account_number"
                  value={formData.account_number}
                  onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                  placeholder="1234567890"
                  required
                  className="font-mono"
                  data-testid="bank-account-number-input"
                />
              </div>
            </div>

            {/* Region-specific fields */}
            <div className="space-y-4 p-4 border border-zinc-200 rounded-lg">
              <p className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">
                {currentConfig?.label} Banking Details
              </p>

              {/* US Fields */}
              {formData.region === 'US' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="routing_number">ACH Routing Number</Label>
                    <Input
                      id="routing_number"
                      value={formData.routing_number}
                      onChange={(e) => setFormData({ ...formData, routing_number: e.target.value })}
                      placeholder="026073150"
                      className="font-mono"
                      data-testid="bank-routing-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="fedwire_routing">Fedwire Routing Number</Label>
                    <Input
                      id="fedwire_routing"
                      value={formData.fedwire_routing}
                      onChange={(e) => setFormData({ ...formData, fedwire_routing: e.target.value })}
                      placeholder="026073008"
                      className="font-mono"
                      data-testid="bank-fedwire-input"
                    />
                  </div>
                </>
              )}

              {/* UK Fields */}
              {formData.region === 'UK' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="sort_code">Sort Code *</Label>
                    <Input
                      id="sort_code"
                      value={formData.sort_code}
                      onChange={(e) => setFormData({ ...formData, sort_code: e.target.value })}
                      placeholder="608382"
                      className="font-mono"
                      data-testid="bank-sort-code-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="iban">IBAN (Optional)</Label>
                    <Input
                      id="iban"
                      value={formData.iban}
                      onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                      placeholder="GB82 WEST 1234 5698 7654 32"
                      className="font-mono"
                      data-testid="bank-iban-input"
                    />
                  </div>
                </>
              )}

              {/* EU Fields */}
              {formData.region === 'EU' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="iban">IBAN *</Label>
                    <Input
                      id="iban"
                      value={formData.iban}
                      onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                      placeholder="DE89 3704 0044 0532 0130 00"
                      className="font-mono"
                      data-testid="bank-iban-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="swift_code">BIC/SWIFT Code</Label>
                    <Input
                      id="swift_code"
                      value={formData.swift_code}
                      onChange={(e) => setFormData({ ...formData, swift_code: e.target.value })}
                      placeholder="COBADEFFXXX"
                      className="font-mono"
                      data-testid="bank-swift-input"
                    />
                  </div>
                </>
              )}

              {/* India Fields */}
              {formData.region === 'IN' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="ifsc_code">IFSC Code (Local Payments)</Label>
                    <Input
                      id="ifsc_code"
                      value={formData.ifsc_code}
                      onChange={(e) => setFormData({ ...formData, ifsc_code: e.target.value })}
                      placeholder="HDFC0004859"
                      className="font-mono"
                      data-testid="bank-ifsc-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="swift_code">SWIFT Code (International)</Label>
                    <Input
                      id="swift_code"
                      value={formData.swift_code}
                      onChange={(e) => setFormData({ ...formData, swift_code: e.target.value })}
                      placeholder="HDFCINBB"
                      className="font-mono"
                      data-testid="bank-swift-input"
                    />
                  </div>
                </>
              )}

              {/* Canada Fields */}
              {formData.region === 'CA' && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="institution_number">Institution Number</Label>
                      <Input
                        id="institution_number"
                        value={formData.institution_number}
                        onChange={(e) => setFormData({ ...formData, institution_number: e.target.value })}
                        placeholder="352"
                        className="font-mono"
                        data-testid="bank-institution-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="transit_number">Transit Number</Label>
                      <Input
                        id="transit_number"
                        value={formData.transit_number}
                        onChange={(e) => setFormData({ ...formData, transit_number: e.target.value })}
                        placeholder="10009"
                        className="font-mono"
                        data-testid="bank-transit-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="routing_number">Routing Number (Optional)</Label>
                    <Input
                      id="routing_number"
                      value={formData.routing_number}
                      onChange={(e) => setFormData({ ...formData, routing_number: e.target.value })}
                      placeholder="035210009"
                      className="font-mono"
                      data-testid="bank-ca-routing-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="swift_code">SWIFT Code (International, Optional)</Label>
                    <Input
                      id="swift_code"
                      value={formData.swift_code}
                      onChange={(e) => setFormData({ ...formData, swift_code: e.target.value })}
                      placeholder="DCBKCATTXXX"
                      className="font-mono"
                      data-testid="bank-swift-input"
                    />
                  </div>
                </>
              )}
            </div>

            {/* Beneficiary Address */}
            <div className="space-y-2">
              <Label htmlFor="address">Beneficiary Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="5 Penn Plaza, 14th Floor, New York, NY 10001, US"
                data-testid="bank-address-input"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" data-testid="save-bank-btn">
                {editingAccount ? 'Update' : 'Add'} Account
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BankAccountsPage;
